import React, { Suspense } from 'react';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { QueryClient } from '@tanstack/react-query';

import { THToastContainer } from '@repo/ui';

import { getImpersonationCookie } from '@repo/utils';
import { Spinfinity, ErrorDisplay } from '@repo/ui';
import { AppErrorBoundary } from '@repo/monitoring';

import { getJWTToken, getTokenUser, JWTUser } from '~/utils/jwt';
import { isSessionExpiredError } from '@repo/api-client';

import AppLayout from '~/components/layouts/AppLayout/AppLayout';
import { ENVIRONMENT } from '~/config/env';

import globalCss from '~/assets/global.css?url';
import 'react-toastify/dist/ReactToastify.min.css';
import { ToastContainer } from 'react-toastify';

const TanStackRouterDevtools =
  ENVIRONMENT !== 'development'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/router-devtools').then(res => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel,
        })),
      );
const ReactQueryDevtools =
  ENVIRONMENT !== 'development'
    ? () => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import('@tanstack/react-query-devtools').then(res => ({
          default: res.ReactQueryDevtools,
        })),
      );

// No need for custom error boundary - using AppErrorBoundary from @repo/monitoring

export const getToken = createServerFn({ method: 'GET' }).handler(async () => {
  const { getWebRequest } = await import('@tanstack/react-start/server');
  // In a server context, we pass the request object to our cookie utilities
  const request = getWebRequest();
  const context = request ? { request } : undefined;
  const token = getJWTToken(context);
  const user = getTokenUser(token);
  return { token, user };
});
// Server function to check for impersonation cookie
export const getImpersonationInfo = createServerFn({ method: 'GET' }).handler(
  async () => {
    const { getWebRequest } = await import('@tanstack/react-start/server');
    // In a server context, we pass the request object to our cookie utilities
    const request = getWebRequest();
    const context = request ? { request } : undefined;

    // Get impersonation cookie using the server context
    const impersonationId = await getImpersonationCookie(context);
    const isImpersonating = !!impersonationId;

    return {
      impersonationId,
      isImpersonating,
    };
  },
);
const RootComponent = () => {
  return (
    <RootDocument>
      {/* <ReactQueryDevtools />
      <TanStackRouterDevtools /> */}
      <div className='absolute'>
        <THToastContainer containerId={'new-container'} />
        <ToastContainer
          className='toast-container'
          toastStyle={{
            backgroundColor: 'hsl(72, 26%, 96%)',
            color: 'hsl(255, 7%, 19%)',
          }}
        />
      </div>
      <AppErrorBoundary
        componentName='partner-portal'
        fallback={({ error, resetError }) => (
          <ErrorDisplay
            error={error}
            reset={resetError}
            onReload={() => window.location.reload()}
            onLogout={() => {
              window.location.href = '/logout';
            }}
            environment={import.meta.env.VITE_APP_ENVIRONMENT}
            description='Please refresh your screen. If you continue to see an error, reach out to hello@tinyhealth.com'
          />
        )}
        sentryTags={{
          app: 'partner-portal',
        }}
        ignoreError={isSessionExpiredError}
      >
        <Suspense fallback={<Spinfinity />}>
          <AppLayout>
            <Outlet />
          </AppLayout>
        </Suspense>
      </AppErrorBoundary>
    </RootDocument>
  );
};

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  isAuthenticated: boolean;
  token?: string;
  user: JWTUser | null;
  isImpersonating?: boolean;
  impersonationId?: string;
}>()({
  beforeLoad: async () => {
    try {
      const { token, user } = await getToken();
      const { impersonationId, isImpersonating } = await getImpersonationInfo();
      return {
        isAuthenticated: !!token,
        token,
        user,
        isImpersonating,
        impersonationId,
      };
    } catch (error) {
      // During logout or when cookies are cleared, server functions may fail.
      // Return unauthenticated state gracefully instead of crashing.
      console.error('[__root__ beforeLoad] Error getting auth state:', error);
      return {
        isAuthenticated: false,
        token: undefined,
        user: null,
        isImpersonating: false,
        impersonationId: undefined,
      };
    }
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Tiny Health Partner Portal',
      },
    ],
    links: [
      {
        rel: 'icon',
        href: '/favicon.svg',
      },
      { rel: 'stylesheet', href: globalCss },
      { rel: 'stylesheet', href: 'assets/style2.css' },
      // Preload for production build (use 'anonymous' as a valid CrossOrigin value)
      ...(import.meta.env.PROD
        ? [
            {
              rel: 'preload',
              href: '/_build/TWKLausanne-300.woff2',
              as: 'font',
              type: 'font/woff2',
              crossOrigin: 'anonymous' as const,
            },
            {
              rel: 'preload',
              href: '/_build/RecifeTextTrial-Regular.woff2',
              as: 'font',
              type: 'font/woff2',
              crossOrigin: 'anonymous' as const,
            },
          ]
        : // Preload for development environment
          [
            {
              rel: 'preload',
              // Use more accurate path for development environment
              href: '/packages/ui/src/assets/fonts/TWKLausanne-300.woff2',
              as: 'font',
              type: 'font/woff2',
              crossOrigin: 'anonymous' as const,
            },
            {
              rel: 'preload',
              href: '/packages/ui/src/assets/fonts/RecifeTextTrial-Regular.woff2',
              as: 'font',
              type: 'font/woff2',
              crossOrigin: 'anonymous' as const,
            },
          ]),
    ],
  }),
  component: RootComponent,
});

const RootDocument = ({ children }: { children: React.ReactNode }) => (
  <>
    <html>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  </>
);
